import { ComponentType, LazyExoticComponent, lazy } from 'react'

const RELOAD_FLAG = 'ubq-chunk-reload'

/**
 * `React.lazy` for route-level code splitting (UBQ-5897).
 *
 * After a deploy the hashed chunk files of the previous build are gone, so a tab that still
 * runs the old `index.html` fails to fetch the chunk it asks for (nginx answers with the SPA
 * fallback, so the import rejects with a bogus "Failed to fetch dynamically imported module").
 * One full reload picks up the new `index.html` and its chunk names. The sessionStorage flag
 * makes it a single attempt, so a genuinely broken chunk surfaces in the ErrorBoundary
 * instead of reload-looping.
 */
export function lazyWithReload(
    factory: () => Promise<{ default: ComponentType }>,
): LazyExoticComponent<ComponentType> {
    return lazy(async () => {
        try {
            const module = await factory()
            try {
                sessionStorage.removeItem(RELOAD_FLAG)
            } catch {
                // sessionStorage can be blocked (private mode); the reload guard just degrades.
            }
            return module
        } catch (error) {
            let alreadyReloaded = true
            try {
                alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === '1'
                if (!alreadyReloaded) {
                    sessionStorage.setItem(RELOAD_FLAG, '1')
                }
            } catch {
                // Storage unavailable: do not risk a reload loop, surface the error.
            }
            if (!alreadyReloaded) {
                window.location.reload()
                // Keep Suspense pending while the page reloads instead of flashing the error UI.
                return new Promise<{ default: ComponentType }>(() => {})
            }
            throw error
        }
    })
}
